import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as create$3, F as FormController } from './form.js';
import { a as BusinessFormStep, B as BusinessFormStepV2 } from './business-form-types.js';
import { a as Business } from './Business.js';
import { A as Api } from './Api.js';
import { c as config } from './config.js';
import { B as voidedCheckValidation, C as governmentIdValidation, D as ss4Validation, E as otherDocumentValidation, F as balanceSheetValidation, G as bankStatementValidation, H as profitAndLossStatementValidation } from './schema-validations.js';
import { a as EntityDocument, b as EntityDocumentStorage } from './Document.js';
import { C as ComponentErrorCodes, a as ComponentErrorSeverity } from './ComponentError.js';
import { S as Skeleton } from './skeleton.js';
import { d as defineCustomElement$6 } from './custom-popper2.js';
import { d as defineCustomElement$5 } from './form-control-error-text2.js';
import { d as defineCustomElement$4 } from './form-control-file2.js';
import { d as defineCustomElement$3 } from './form-control-tooltip2.js';
import { d as defineCustomElement$2 } from './business-document-upload-input-group.js';
import { d as defineCustomElement$1 } from './business-documents-on-file.js';

const businessDocumentSchema = (volume, documents, allowOptionalFields) => {
    const schema = create$3({
        voided_check: voidedCheckValidation(documents, allowOptionalFields),
        government_id: governmentIdValidation.nullable(),
        ss4: ss4Validation.nullable(),
        other: otherDocumentValidation.nullable(),
        balance_sheet: balanceSheetValidation(volume, documents, allowOptionalFields),
        bank_statement: bankStatementValidation(volume, documents, allowOptionalFields),
        profit_and_loss_statement: profitAndLossStatementValidation(volume, documents, allowOptionalFields),
    });
    const easySchema = create$3({
        voided_check: voidedCheckValidation(documents, allowOptionalFields),
        government_id: governmentIdValidation.nullable(),
        ss4: ss4Validation.nullable(),
        other: otherDocumentValidation.nullable(),
        balance_sheet: balanceSheetValidation(volume, documents, allowOptionalFields),
        bank_statement: bankStatementValidation(volume, documents, allowOptionalFields),
        profit_and_loss_statement: profitAndLossStatementValidation(volume, documents, allowOptionalFields)
    });
    return allowOptionalFields ? easySchema : schema;
};

const BusinessDocumentFormStep = /*@__PURE__*/ proxyCustomElement(class BusinessDocumentFormStep extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.fetchData = async () => {
            this.renderState = 'loading';
            this.formLoading.emit(true);
            try {
                const response = await this.api.get(this.businessEndpoint);
                this.business = Object.assign({}, new Business(response.data));
                this.existingDocuments = response.data.documents;
                this.paymentVolume = response.data.additional_questions.business_payment_volume;
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.FETCH_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
                this.renderState = 'error';
            }
            finally {
                this.initializeFormController();
                this.formLoading.emit(false);
                this.renderState = 'success';
            }
        };
        this.initializeFormController = () => {
            this.formController = new FormController(businessDocumentSchema(this.paymentVolume, this.existingDocuments, this.allowOptionalFields));
            this.formController.errors.subscribe(errors => {
                this.errors = Object.assign({}, errors);
            });
        };
        this.createDocumentRecord = async (docData) => {
            this.formLoading.emit(true);
            const payload = docData.record_data;
            try {
                const response = await this.api.post(this.documentEndpoint, payload);
                return this.handleDocRecordResponse(docData, response);
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
                return false;
            }
        };
        this.handleDocRecordResponse = (docData, response) => {
            if (response.error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: response.error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: response.error,
                });
                return false;
            }
            else {
                docData.setPresignedUrl(response.data.presigned_url);
                return true;
            }
        };
        this.uploadDocument = async (docData) => {
            if (!docData.presigned_url) {
                throw new Error('Presigned URL is not set');
            }
            const response = await fetch(docData.presigned_url, {
                method: 'PUT',
                body: docData.fileString,
            });
            return this.handleUploadResponse(response);
        };
        this.handleUploadResponse = (response) => {
            if (response.error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: response.error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: response.error,
                });
                return false;
            }
            else {
                this.submitted.emit({ data: response, metadata: { completedStep: BusinessFormStep.documentUpload } });
                this.stepCompleted.emit({ data: response, formStep: BusinessFormStepV2.documentUpload });
                return true;
            }
        };
        this.storeFiles = (e) => {
            const fileList = Array.from(e.detail.fileList);
            const docType = e.detail.document_type;
            const documentList = fileList.map(file => new EntityDocument({ file, document_type: docType }, this.businessId));
            this.documentData[docType] = documentList;
        };
        this.sendData = async (onSuccess) => {
            try {
                const docArray = Object.values(this.documentData).flat();
                if (!docArray.length) {
                    this.stepCompleted.emit({ data: null, formStep: BusinessFormStepV2.documentUpload, metadata: 'no data submitted' });
                    return onSuccess();
                }
                const documentRecords = docArray.map(docData => this.createDocumentRecord(docData));
                const recordsCreated = await Promise.all(documentRecords);
                if (!recordsCreated) {
                    return;
                }
                const uploads = docArray.map(docData => this.uploadDocument(docData));
                const uploadsCompleted = await Promise.all(uploads);
                if (!uploadsCompleted) {
                    return;
                }
                await onSuccess();
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.formController = undefined;
        this.errors = {};
        this.existingDocuments = [];
        this.paymentVolume = undefined;
        this.business = undefined;
        this.documentData = new EntityDocumentStorage();
        this.renderState = 'loading';
        this.authToken = undefined;
        this.businessId = undefined;
        this.allowOptionalFields = undefined;
    }
    get businessEndpoint() {
        return `entities/business/${this.businessId}`;
    }
    get documentEndpoint() {
        return 'entities/document';
    }
    componentWillLoad() {
        this.api = Api({ authToken: this.authToken, apiOrigin: config.proxyApiOrigin });
        if (this.businessId && this.authToken) {
            this.fetchData();
        }
    }
    async validateAndSubmit({ onSuccess }) {
        this.formController.validateAndSubmit(() => this.sendData(onSuccess));
    }
    get isLoading() {
        return this.renderState === 'loading';
    }
    get isError() {
        return this.renderState === 'error';
    }
    get documentsOnFile() {
        if (this.isError) {
            return null;
        }
        if (this.isLoading) {
            return h(Skeleton, { variant: 'rounded', height: '50px' });
        }
        return h("justifi-business-documents-on-file", { documents: this.existingDocuments });
    }
    get formInputs() {
        if (this.isError) {
            return null;
        }
        if (this.isLoading) {
            return h(Skeleton, { variant: 'rounded', height: '350px' });
        }
        return (h("justifi-business-document-upload-input-group", { paymentVolume: this.paymentVolume, inputHandler: this.inputHandler, storeFiles: this.storeFiles, errors: this.errors }));
    }
    render() {
        return (h("form", { key: '5e0722b4d5ea462cf5ba6ed2de9d3a73b8bf910a' }, h("fieldset", { key: '09f3589465a6ad53080743018a57b959bef68309' }, h("div", { key: 'b6c1aa4cf4754126b11902a8b35c19e3044ba4ed', class: "d-flex align-items-center gap-2" }, h("legend", { key: '77b5d6847054afbfe6cc7e9e00762c226a582447', class: "mb-0" }, "Document Uploads"), h("form-control-tooltip", { key: '9fb518fbad5039c77436377e26e420dc01a84ca2', helpText: "Various file formats such as PDF, DOC, DOCX, JPEG, and others are accepted. Multiple files can be uploaded for each document category." })), h("hr", { key: 'e95938b42ad6d52d612bb654e39af7ee3442b454', class: "mt-2" }), this.documentsOnFile, h("div", { key: '8ee1cda96d28486faa2371bb1b82c57a57220071', class: "d-flex flex-column" }, this.formInputs))));
    }
}, [0, "justifi-business-document-upload-form-step", {
        "authToken": [1, "auth-token"],
        "businessId": [1, "business-id"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "existingDocuments": [32],
        "paymentVolume": [32],
        "business": [32],
        "documentData": [32],
        "renderState": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-document-upload-form-step", "custom-popper", "form-control-error-text", "form-control-file", "form-control-tooltip", "justifi-business-document-upload-input-group", "justifi-business-documents-on-file"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-document-upload-form-step":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessDocumentFormStep);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-file":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "justifi-business-document-upload-input-group":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "justifi-business-documents-on-file":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessDocumentFormStep as B, defineCustomElement as d };
